import { useEffect, useLayoutEffect, useRef, useState, type FC, type KeyboardEvent } from 'react'
import { Icon } from './Icon.js'
import { useFieldContext } from './Field.js'

export interface LookupOption {
  value: string
  label: string
  sublabel?: string
  /** What else the pick carries — a material's unit and HSN/SAC. */
  extra?: Readonly<Record<string, string | null>>
}

export interface LookupProps {
  /** Master entity key, e.g. 'supplier', 'project', 'purchase_order'. */
  entity: string
  value: string
  onChange: (value: string, option: LookupOption | null) => void
  placeholder?: string
  /** Display text for an already-set value, when the page knows it. */
  label?: string
  disabled?: boolean
  invalid?: boolean
  /**
   * Further query parameters every search sends — `owners` narrows an address
   * or contact picker to one party's records (BILL-008).
   */
  params?: Readonly<Record<string, string>>
  /** List without typing: the picker opens on what it may choose from. */
  browse?: boolean
  /**
   * Set when there is nothing to choose from yet — a supplier's contact before
   * a supplier is chosen. The picker says so and searches nothing.
   */
  emptyMessage?: string
}

const asString = (v: unknown): string | undefined =>
  typeof v === 'string' ? v : typeof v === 'number' ? String(v) : undefined

/**
 * The lookup endpoint is shared by 25 document types and several masters, so
 * accept the shapes it may reasonably return rather than breaking a whole form
 * over a key name.
 */
const normalise = (raw: unknown): LookupOption[] => {
  const list = Array.isArray(raw)
    ? raw
    : raw && typeof raw === 'object'
      ? ((raw as Record<string, unknown>)['items'] ?? (raw as Record<string, unknown>)['results'] ?? (raw as Record<string, unknown>)['rows'])
      : undefined

  if (!Array.isArray(list)) return []

  const out: LookupOption[] = []
  for (const entry of list) {
    if (!entry || typeof entry !== 'object') continue
    const row = entry as Record<string, unknown>
    const value = asString(row['value']) ?? asString(row['id'])
    const text = asString(row['label']) ?? asString(row['name']) ?? asString(row['title']) ?? value
    if (value === undefined || text === undefined) continue
    const sub = asString(row['secondary']) ?? asString(row['sublabel']) ?? asString(row['description'])
    const extra = row['extra'] && typeof row['extra'] === 'object'
      ? (row['extra'] as Record<string, string | null>)
      : undefined
    out.push({ value, label: text, ...(sub ? { sublabel: sub } : {}), ...(extra ? { extra } : {}) })
  }
  return out
}

export const Lookup: FC<LookupProps> = ({
  entity,
  value,
  onChange,
  placeholder = 'Please start typing to search',
  label,
  disabled,
  invalid,
  params,
  browse = false,
  emptyMessage,
}) => {
  const extraQuery = params
    ? Object.entries(params).map(([k, v]) => `&${encodeURIComponent(k)}=${encodeURIComponent(v)}`).join('')
    : ''
  const field = useFieldContext()
  const bad = invalid ?? field?.invalid ?? false

  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [options, setOptions] = useState<LookupOption[]>([])
  const [busy, setBusy] = useState(false)
  const [searched, setSearched] = useState(false)
  const [failed, setFailed] = useState(false)
  const [active, setActive] = useState(0)
  const [chosen, setChosen] = useState(label ?? '')

  const inputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (label !== undefined) setChosen(label)
  }, [label])

  useEffect(() => {
    if (!value) setChosen('')
  }, [value])

  /*
    A value that arrives without its label — a report or dashboard opened from
    a link with a project already chosen — used to show the bare id. Ask for
    the one row it names, through the same scoping a search uses.
  */
  useEffect(() => {
    if (!value || label !== undefined || chosen !== '') return undefined
    const ctrl = new AbortController()
    fetch(`/api/lookups/${encodeURIComponent(entity)}?id=${encodeURIComponent(value)}`, {
      signal: ctrl.signal,
      headers: { accept: 'application/json' },
    })
      .then(async (res) => (res.ok ? normalise(await res.json()) : []))
      .then((rows) => {
        const row = rows.find((r) => r.value === value)
        if (row) setChosen(row.label)
      })
      .catch(() => undefined)
    return () => ctrl.abort()
  }, [entity, value, label, chosen])

  useEffect(() => {
    if (!open) return undefined

    const q = query.trim()
    if (emptyMessage !== undefined || (q.length === 0 && !browse)) {
      setOptions([])
      setSearched(false)
      setFailed(false)
      setBusy(false)
      return undefined
    }

    const ctrl = new AbortController()
    const timer = setTimeout(() => {
      setBusy(true)
      fetch(`/api/lookups/${encodeURIComponent(entity)}?q=${encodeURIComponent(q)}${extraQuery}`, {
        signal: ctrl.signal,
        headers: { accept: 'application/json' },
      })
        .then(async (res) => {
          if (!res.ok) throw new Error(`Lookup responded ${res.status}`)
          return (await res.json()) as unknown
        })
        .then((body) => {
          setOptions(normalise(body))
          setActive(0)
          setFailed(false)
          setSearched(true)
          setBusy(false)
        })
        .catch((err: unknown) => {
          if (err instanceof DOMException && err.name === 'AbortError') return
          setOptions([])
          setFailed(true)
          setSearched(true)
          setBusy(false)
        })
    }, q.length === 0 ? 0 : 250)

    return () => {
      clearTimeout(timer)
      ctrl.abort()
    }
  }, [entity, query, open, extraQuery, browse, emptyMessage])

  const close = () => {
    setOpen(false)
    setQuery('')
    setOptions([])
    setSearched(false)
    setFailed(false)
  }

  const pick = (option: LookupOption) => {
    setChosen(option.label)
    onChange(option.value, option)
    close()
    inputRef.current?.focus()
  }

  const clear = () => {
    setChosen('')
    onChange('', null)
    close()
    inputRef.current?.focus()
  }

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      close()
      return
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if (options.length === 0) return
      e.preventDefault()
      setActive((i) => {
        const next = e.key === 'ArrowDown' ? i + 1 : i - 1
        return (next + options.length) % options.length
      })
      return
    }
    if (e.key === 'Enter') {
      const option = options[active]
      if (open && option) {
        e.preventDefault()
        pick(option)
      }
    }
  }

  const message = emptyMessage !== undefined
    ? emptyMessage
    : failed
      ? 'The search could not be completed. Try again.'
      : busy
        ? 'Searching…'
        : query.trim().length === 0 && !browse
          ? 'Start typing to search'
          : searched && options.length === 0
            ? (browse && query.trim().length === 0 ? 'No entries' : 'No results found')
            : null

  const listId = `${field?.id ?? entity}-lookup-list`

  /**
   * The option list is positioned against the viewport, not the input.
   *
   * An absolutely-positioned menu is clipped by any scrolling ancestor, and
   * the create wizard's body is exactly that — so inside a modal the options
   * rendered underneath the action bar and could not be read. Measuring and
   * fixing to the viewport escapes every overflow ancestor.
   */
  const [menuBox, setMenuBox] = useState<{ top: number; left: number; width: number } | null>(null)
  useLayoutEffect(() => {
    if (!open) { setMenuBox(null); return }
    const measure = (): void => {
      const el = inputRef.current?.closest('.lookup')
      if (!el) return
      const r = el.getBoundingClientRect()
      const below = window.innerHeight - r.bottom
      // Flip above the field when there is more room there.
      setMenuBox(
        below < 160 && r.top > below
          ? { top: Math.max(8, r.top - Math.min(320, r.top - 12)), left: r.left, width: r.width }
          : { top: r.bottom + 2, left: r.left, width: r.width },
      )
    }
    measure()
    window.addEventListener('scroll', measure, true)
    window.addEventListener('resize', measure)
    return () => {
      window.removeEventListener('scroll', measure, true)
      window.removeEventListener('resize', measure)
    }
  }, [open, options.length])

  return (
    <div
      className="lookup"
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) close()
      }}
    >
      <span className="control-wrap">
        <span className="control__icon">
          <Icon name="search" size={18} />
        </span>
        <input
          ref={inputRef}
          id={field?.id}
          type="text"
          autoComplete="off"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-invalid={bad || undefined}
          aria-describedby={field?.describedBy}
          aria-required={field?.required || undefined}
          disabled={disabled}
          className={`control control--with-icon${value ? ' control--with-clear' : ''}${bad ? ' control--invalid' : ''}`}
          value={open ? query : chosen || (value ? value : '')}
          placeholder={chosen || placeholder}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setOpen(true)
            setQuery(e.currentTarget.value)
          }}
          onKeyDown={onKeyDown}
        />
        {value && !disabled ? (
          <button type="button" className="lookup__clear" onClick={clear} aria-label="Clear selection">
            <Icon name="x" size={18} />
          </button>
        ) : null}
      </span>

      {open ? (
        <div
          className="lookup__menu"
          id={listId}
          role="listbox"
          style={menuBox ? { position: 'fixed', top: menuBox.top, left: menuBox.left, width: menuBox.width, right: 'auto' } : undefined}
          onMouseDown={(e) => e.preventDefault()}
        >
          {message ? <div className="lookup__msg">{message}</div> : null}
          {options.map((o, i) => (
            <button
              key={o.value}
              type="button"
              role="option"
              aria-selected={o.value === value}
              className={`lookup__opt${i === active ? ' lookup__opt--active' : ''}`}
              onMouseEnter={() => setActive(i)}
              onClick={() => pick(o)}
            >
              {o.label}
              {o.sublabel ? <span className="lookup__sub">{o.sublabel}</span> : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  )
}
